/**
 * Pure page-fitting math for the Studio's paginated canvas. No DOM: the
 * pagination plugin measures breakable heights and hands them here; this
 * module decides where pages end.
 *
 * A breakable is the unit between which a page may break (top-level block,
 * list item, table row — paragraphs never split). Shape:
 *   { heightPx: number,
 *     hints: { pageBreakBefore?, keepNext?, forceBreakAfter? },
 *     leadInPx?: number,
 *     footnotePx?: number }
 *
 * keepNext chains move transitively (capped) so a heading never strands at
 * a page bottom. A unit taller than one page gets its own STRETCHED page —
 * content never bleeds into the inter-page gap.
 *
 * leadInPx is a capacity charge applied when the unit STARTS a page:
 * reserved space at the top of that page (e.g. a repeated table header).
 * The page carries it as page.leadInPx, its usedPx INCLUDES it (so the
 * stretched/heightPx/unused math needs no special cases), and the spacer
 * BEFORE the page grows by it so content flow resumes below the reserve.
 *
 * footnotePx is a capacity charge at the BOTTOM of whichever page the unit
 * lands on (Word's footnote area: the unit and its footnotes travel to the
 * next page together when they no longer share one). It accumulates into
 * page.footnotePx but stays OUT of usedPx — the flow's physical extent —
 * so the spacer's unused span (heightPx − usedPx) automatically covers the
 * reserved area, which the chrome paints at the card bottom. Charged per
 * unit including a flat separator allowance; two footnote-carrying units
 * sharing a page over-reserve by one separator — an accepted, safe-side
 * v1 approximation (breaks a line early rather than ever overlapping).
 */

const KEEP_NEXT_CHAIN_CAP = 10

/**
 * @param breakables list of {heightPx, hints, leadInPx?, footnotePx?}
 * @param contentHeightPx usable page body height — a number, or a
 *   (pageIndex) => number when pages differ (Word grows a page's margins to
 *   clear its header/footer, and titlePg first-page chrome differs from the
 *   rest)
 * @returns pages: [{start, end, usedPx, heightPx, stretched, leadInPx,
 *   footnotePx}] — end exclusive; usedPx includes the page's lead-in but
 *   NOT its footnote reserve (see the module docblock)
 */
export function fitPages(breakables, contentHeightPx) {
  const capacityFor =
    typeof contentHeightPx === 'function' ? contentHeightPx : () => contentHeightPx
  if (breakables.length === 0 || capacityFor(0) <= 0) {
    return [
      {
        start: 0,
        end: breakables.length,
        usedPx: 0,
        heightPx: capacityFor(0),
        stretched: false,
        leadInPx: 0,
        footnotePx: 0,
      },
    ]
  }

  const pages = []
  let start = 0
  // Every page — the first included — seeds its running total with the
  // lead-in of the unit that starts it; the keepNext pull-back re-add loop
  // below then lands on this already-reduced capacity.
  let leadIn = breakables[0].leadInPx || 0
  let used = leadIn
  let fnReserve = 0
  let index = 0

  const close = (end, nextStart) => {
    const capacity = capacityFor(pages.length)
    const capacityUsed = used + fnReserve
    const stretched = capacityUsed > capacity
    pages.push({
      start,
      end,
      usedPx: used,
      heightPx: Math.max(capacityUsed, capacity),
      stretched,
      leadInPx: leadIn,
      footnotePx: fnReserve,
    })
    start = nextStart
    leadIn = breakables[nextStart]?.leadInPx || 0
    used = leadIn
    fnReserve = 0
  }

  while (index < breakables.length) {
    const unit = breakables[index]
    const onPageAlready = index > start
    const forcedBefore =
      onPageAlready && (unit.hints?.pageBreakBefore || breakables[index - 1]?.hints?.forceBreakAfter)
    const overflow =
      onPageAlready &&
      used + fnReserve + unit.heightPx + (unit.footnotePx || 0) > capacityFor(pages.length)

    if (forcedBefore || overflow) {
      // Pull back over a keepNext chain so its members travel together.
      let breakAt = index
      let chain = 0
      while (
        breakAt > start &&
        chain < KEEP_NEXT_CHAIN_CAP &&
        breakables[breakAt - 1]?.hints?.keepNext &&
        !breakables[breakAt - 1]?.hints?.forceBreakAfter
      ) {
        breakAt--
        chain++
      }
      // A page consisting solely of an unbreakable chain stays put.
      if (breakAt === start) breakAt = index

      for (let i = breakAt; i < index; i++) {
        used -= breakables[i].heightPx
        fnReserve -= breakables[i].footnotePx || 0
      }
      close(breakAt, breakAt)
      for (let i = breakAt; i < index; i++) {
        used += breakables[i].heightPx
        fnReserve += breakables[i].footnotePx || 0
      }
    }

    used += unit.heightPx
    fnReserve += unit.footnotePx || 0
    index++
  }

  close(breakables.length, breakables.length)
  return pages
}

/**
 * Spacer heights for the gaps between fitted pages: the unused space at the
 * bottom of page N + bottom margin + inter-page gap + top margin (+ any
 * header/footer extension) of page N+1, plus page N+1's lead-in reserve so
 * content flow resumes below the reserved space.
 *
 * geometry carries either constant marginTopPx/marginBottomPx, or an
 * insetsFor(pageIndex) => {topPx, bottomPx} when the effective margins vary
 * per page (tall chrome, titlePg first-page parts).
 *
 * @returns [{beforeIndex, heightPx}] — one per page boundary; beforeIndex is
 *   the breakable index that starts the following page.
 */
export function pageSpacers(pages, geometry) {
  const { marginTopPx, marginBottomPx, gapPx } = geometry
  const insetsFor =
    geometry.insetsFor ?? (() => ({ topPx: marginTopPx, bottomPx: marginBottomPx }))
  const spacers = []

  for (let i = 0; i < pages.length - 1; i++) {
    const page = pages[i]
    const unused = Math.max(0, page.heightPx - page.usedPx)
    spacers.push({
      beforeIndex: pages[i + 1].start,
      heightPx: Math.round(
        unused + insetsFor(i).bottomPx + gapPx + insetsFor(i + 1).topPx + (pages[i + 1].leadInPx || 0),
      ),
    })
  }

  return spacers
}

/** mm → CSS px at 96dpi. */
export function mmToPx(mm) {
  return (Number(mm) * 96) / 25.4
}
